import React from 'react'
import { useSelector } from 'react-redux'
import { FormattedMessage } from 'react-intl'

// Components
import { Format, If, Text } from '../../../..'
import { Player } from '../../../../shared/Player'
import { Heading, ProConList, Pro, Info, Strong } from '../../../../shared/Gem/GemDetail'
import MonthlyPayment from '../../MonthlyPayment'
import { BuyersLink } from '../../BuyersLink'
import CTAHeading from '../../CTAHeading'

// Selectors
import {
  selectNewPrimaryResidenceDownPayment,
  selectNewPrimaryResidenceBudget,
  selectNewPrimaryResidenceMonthlyPayment
} from '../../../../../store/selectors/home/equity'
import { selectShowBuyersLink } from '../../../../../store/selectors/routes'
import { selectNewPrimaryResidenceMortgageRate } from '../../../../../store/selectors/mortgageRate'
import { selectRentHomeVideoId, selectRentHomeVideoType } from '../../../../../store/selectors/videos'

// Utils
import { rentBuy } from '../../../../../constants/equity'
import { FROM } from '../../../../../mappings/navigation'

// Styles
import styles from './RentBuyDetail.module.scss'
import { HOMEOWNER_DIGEST_PURCHASING_POWER_RENT_AND_BUY_ANOTHER_PANEL } from '../../../../../constants/tracking/trackingEventLocations/homeownerDigestTrackingEventLocation'

const { RENT_BUY } = FROM

const RentBuy = () => {
  const showBuyersLink = useSelector(selectShowBuyersLink)
  const budget = useSelector(selectNewPrimaryResidenceBudget)
  const downPaymentAmount = useSelector(selectNewPrimaryResidenceDownPayment)
  const monthlyPayment = useSelector(selectNewPrimaryResidenceMonthlyPayment)
  const mortgageRate = useSelector(selectNewPrimaryResidenceMortgageRate)

  const videoId = useSelector(selectRentHomeVideoId)
  const videoType = useSelector(selectRentHomeVideoType)

  return (
    <div>
      <Heading>
        <FormattedMessage
          id='Homeowner.purchasePower.rentBuyHeading'
          defaultMessage='You could take around {downPaymentAmount} of your equity and use it as a {downPaymentPercent} down payment on another home worth {budget} and rent out your current house.'
          values={{
            downPaymentAmount: (
              <Strong>
                <Format.Currency abbreviate value={downPaymentAmount} />
              </Strong>
            ),
            downPaymentPercent: (
              <Strong>
                <Format.Percentage value={rentBuy.downPaymentPercent * 100} />
              </Strong>
            ),
            budget: (
              <Strong>
                <Format.Currency abbreviate value={budget} />
              </Strong>
            )
          }}
        />
      </Heading>
      <If condition={videoType && videoId}>
        <Player className={styles.video} videoType={videoType} videoId={videoId} />
      </If>
      <ProConList className={styles.proCon}>
        <Pro>
          <FormattedMessage
            id='Homeowner.purchasePower.rentBuyOne'
            defaultMessage='Your new monthly payment on a {loanTerm}-year mortgage at {apr} would be around {payment}'
            values={{
              apr: <Format.Rate value={mortgageRate} />,
              loanTerm: rentBuy.loanTerm,
              payment: <MonthlyPayment value={monthlyPayment} />
            }}
          />
        </Pro>
        <Pro>
          <FormattedMessage
            id='Homeowner.purchasePower.rentBuyFour'
            defaultMessage='You could lease the property long-term or rent it nightly on Airbnb (Always check with a real estate agent first)'
          />
        </Pro>
        <Pro>
          <FormattedMessage
            id='Homeowner.purchasePower.rentBuyThree'
            defaultMessage='Use rental income to cover some or all of your original mortgage payment'
          />
        </Pro>
        <Info>
          <FormattedMessage
            id='Homeowner.purchasePower.rentBuyFive'
            defaultMessage='Consider whether you have time to be a landlord (or should hire a property manager)'
          />
        </Info>
      </ProConList>

      <If condition={showBuyersLink}>
        <CTAHeading className={styles.cta}>
          <FormattedMessage id='Homeowner.purchasePower.rentBuyCTA' defaultMessage='Find your new home' />
        </CTAHeading>
        <Text align='center' variant='neutralCool' variantCount='900'>
          <BuyersLink
            affordability={budget}
            from={RENT_BUY}
            tracking={{
              guid: 'qbqgL9QYK3KBxsXF8M7yec',
              ui_context: HOMEOWNER_DIGEST_PURCHASING_POWER_RENT_AND_BUY_ANOTHER_PANEL
            }}
          />
        </Text>
      </If>
    </div>
  )
}

export default RentBuy
